import { SimpleBrandEntity } from '../brand.types';
import { LocationMode, SimpleLocationEntity, WeekDay } from '../location.types';
import type { ProviderConnectionType } from './location.types';
import {
  Availability,
  MenuOptionPriceCustomizationV2Response,
  MenuV2Response,
} from '../menu.types';
import { Provider } from '../shared.types';
import { UserRole } from '../user.types';

export type MenuProviderEntity = {
  provider: Provider;
  price: number;
  description: string;
};

export type MenuEntity = {
  name: string;
  providers: Array<MenuProviderEntity>;
};

export type CreateMenuProvider = {
  name: string;
  image: string | null;
  /** The Manage Providers marketplace this row stands for; unique across rows. */
  type?: string | null;
};

export type MenuSignInPropertyEntity = CreateMenuProvider & {
  id: string;
  requiresPassword?: boolean;
  portalUrl?: string | null;
  portalLabel?: string | null;
};

export type MenuNamesResponse = {
  brandName: string;
  locationName: string;
  location: SimpleLocationEntity;
  brand?: SimpleBrandEntity | null;
  locationMode: LocationMode;
  allowedToEditMenuPrice: boolean;
  pushOrdersToPos?: boolean;
};

export type PublishMenuDto = {
  enablePushOrdersToPos: boolean;
};

export type ImportMenuV2Dto = {
  categories: ImportMenuCategoryV2Dto[];
  modifierGroups: ImportMenuModifierGroupV2Dto[];
  schedules: ImportMenuScheduleV2Dto[];
  previewUrl: string | null;
  locationId?: number | null;
  brandId?: number | null;
};

// --- AI menu import (upload a document + optional prompt, worker parses it) ---
export type MenuImportAiStatus = 'processing' | 'ready' | 'failed';

export type MenuImportAiStartResponse = {
  jobId: string;
  status: MenuImportAiStatus;
};

export type MenuImportAiPreviewResponse = {
  status: MenuImportAiStatus;
  // present only when status === 'ready'
  data?: ImportMenuV2Dto | null;
  // present only when status === 'failed'
  error?: string | null;
};

export type ImportMenuCategoryV2Dto = {
  name: string;
  description: string;
  scheduleIds: number[];
  items: {
    menuItem: CreateMenuItemWithModifiersV2Dto;
    imageUrl?: string | null;
  }[];
};

export type CreateMenuItemWithModifiersV2Dto = {
  name: string;
  description: string;
  price: number;
  pickupPrice: number;
  categoryId: number;
  modifierGroupIds: number[];
  customizedPrices: MenuItemProviderPriceV2Dto[];
  availability: Availability;
  preparationNotes?: string | null;
};

export type MenuItemProviderPriceV2Dto = {
  provider: Provider;
  price: number;
  pickupPrice?: number | null;
};

export type ImportMenuModifierGroupV2Dto = {
  id: number;
  name: string;
  description: string;
  type: ModifierType;
  options: CreateMenuOptionV2Dto[];
  required: boolean;
  selectionsMin: number;
  selectionsMax: number;
};

export type ImportMenuScheduleV2Dto = {
  id: number;
  name: string;
  hours: MenuScheduleV2Dto[];
};

export type CreateMenuCategoryV2Dto = {
  name: string;
  description: string;
  scheduleIds: number[];
};

export type UpdateMenuCategoryV2Dto = {
  name?: string;
  description?: string;
  scheduleIds?: number[];
};

export type CreateMenuModifierV2Dto = {
  name: string;
  description: string;
  type: ModifierType;
  options: CreateMenuOptionV2Dto[];
  required: boolean;
  selectionsMin: number;
  selectionsMax: number;
};

export type CreateMenuOptionV2Dto = {
  name: string;
  description?: null | string;
  price: number;
  availability: Availability;
  customizedPrices?: MenuOptionPriceCustomizationV2Response[];
};

export enum ModifierType {
  Price = 'price',
  Option = 'option',
}

export type UpdateMenuModifierGroupV2Dto = {
  name?: string;
  description?: string;
  type?: ModifierType;
  isPriceGroup?: boolean;
  options: CreateMenuOptionV2Dto[];
  required?: boolean;
  selectionsMin?: number;
  selectionsMax?: number;
};

export type CreateMenuScheduleRequestData = {
  name: string;
  hours: {
    day: number;
    startTime: string;
    endTime: string;
  }[];
};

export type UpdateMenuScheduleV2Dto = {
  name: string;
  hours: MenuScheduleV2Dto[];
};

export type MenuScheduleV2Dto = {
  day: WeekDay;
  startTime: string;
  endTime: string;
};

// --- Several menus per brand (multi-menu) ---

/**
 * One menu of a brand, as `GET v2/menu/:locationId/menus/:brandId` lists them:
 * the default first, the rest in creation order. `providers` are the providers
 * currently publishing this menu, including the ones with no assignment of
 * their own that simply follow the default. `shouldPublish` / `lastPublishedAt`
 * are per menu, so each menu gets its own Publish state.
 */
export type BrandMenuResponse = {
  id: number;
  name: string;
  isDefault: boolean;
  shouldPublish: boolean;
  lastPublishedAt: string | null;
  providers: Provider[];
  /**
   * The brand's flag, repeated on every row (the backend team sets it, it is
   * never sent from here). While it is false the list holds only the default
   * menu and every route below rejects a non-default `menuId` with a 400.
   */
  multiMenuEnabled: boolean;
};

export type CreateBrandMenuDto = {
  /** Required, max 100 chars, unique per brand (case-insensitive). */
  name: string;
  /** Copies categories, items, modifiers, schedules, prices and POS links. */
  copyFromMenuId?: number;
};

export type UpdateBrandMenuDto = {
  name?: string;
  /** Only `true` is accepted: the default is switched, never unset. */
  isDefault?: true;
};

/**
 * One connected, publish-capable provider and the menu it publishes.
 * `assigned: false` means it has no assignment of its own and follows the
 * default menu. Every provider with `kitchenHubGroup: true` shares one menu
 * with the others in that group, so the UI shows them as a single choice.
 */
export type BrandMenuProviderResponse = {
  provider: Provider;
  connectionType: ProviderConnectionType;
  kitchenHubGroup: boolean;
  menuId: number;
  menuName: string;
  assigned: boolean;
};

export type BrandMenuProviderAssignmentDto = {
  provider: Provider;
  menuId: number;
};

/** Partial: only the listed providers change, the rest keep their menu. */
export type UpdateBrandMenuProvidersDto = {
  assignments: BrandMenuProviderAssignmentDto[];
};

export type GetPublishSyncStatusResponse = {
  shouldPublish: boolean;
  syncList: GetSyncStatusResponse[];
  lastPublishedAt?: string | null;
};

export type GetSyncStatusResponse = {
  provider: Provider;
  status: SyncStatus;
  errorMessage?: string | null;
};

export enum SyncStatus {
  Completed = 'completed',
  Failed = 'failed',
  InProgress = 'inProgress',
  Waiting = 'waiting',
  Pending = 'pending',
}

export type MenuPublishHistoryPublishedByPhone = {
  number: string;
  nationalNumber: string;
  isUSNumber: boolean;
};

export type MenuPublishHistoryPublishedBy = {
  id: number;
  firstName: string | null;
  lastName: string | null;
  role: UserRole;
  createdAt: string;
  phone: MenuPublishHistoryPublishedByPhone;
};

export type MenuPublishHistoryItemResponse = {
  id: number;
  version: number;
  snapshotSchemaVersion: number;
  snapshotSizeBytes: number;
  publishedBy: MenuPublishHistoryPublishedBy | null;
  publishedAt: string;
  restoredBy: MenuPublishHistoryPublishedBy | null;
  restoredAt: string | null;
};

export type MenuPublishHistoryDetailResponse = {
  id: number;
  version: number;
  snapshotSchemaVersion: number;
  snapshotSizeBytes: number;
  publishedBy: MenuPublishHistoryPublishedBy | null;
  publishedAt: string;
  snapshot: MenuV2Response;
};
